import BaseImage from "@/app/(site)/_components/Atoms/BaseImage"
import IconArrowRight from "@/app/(site)/_components/Atoms/Icons/IconArrowRight"
import StoryText from "@/app/(site)/_components/Atoms/Storytext"
import Text from "@/app/(site)/_components/Atoms/Text"
import ButtonText from "@/app/(site)/_components/Molecules/ButtonText"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { getArticleHref } from "@/app/(site)/_utils/misc"
import { FOCUS_VISIBLE_CARD_CLASS } from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"
import { getLangCode } from "@/src/utils/string/getLangCode"
import { t } from "@/translations"
import { type RelatedArticles } from "@/types"

export default function ArticleCard({
    article,
    index,
    locale,
    isAboveTheFold
}: {
    article: RelatedArticles
    index: number
    locale: string
    isAboveTheFold?: boolean
}) {
    const isPressroom = article?.preSlug === "pressroom/"
    const pressromPageArticle = article._type === "pressroomArticle"
    const datePublished = article.articleCreationDate
    const formattedDate = datePublished
        ? new Date(datePublished).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric"
          })
        : null
    const language = getLangCode(locale) || "en"
    const articleHref = getArticleHref(article, language)

    return (
        <article data-article-index={index} className={FOCUS_VISIBLE_CARD_CLASS}>
            {/* One tab stop per card: the heading inside names the link, so the
                image is decorative and "Read more" is taken out of tab order.
                The ring is drawn on the card, not here — see the class above. */}
            <DynamicLink href={articleHref} prefetch={false} className="block">
                {article.image?.asset && (
                    <figure
                        className={clsx(
                            "relative w-full overflow-hidden",
                            article?.image?.squareImage ? "aspect-square" : "aspect-[3/4]"
                        )}
                    >
                        <BaseImage
                            className="object-cover"
                            alt=""
                            asset={article.image?.asset}
                            src={article.image?.asset?._ref || ""}
                            mimeType={article.image?.asset?.mimeType}
                            fill
                            isAboveTheFold={isAboveTheFold}
                            sizes="(max-width: 1023px) 80px, 40vw"
                        />
                    </figure>
                )}
                <StoryText size="size2" className="mt-24">
                    {article?.pageHeading}
                </StoryText>
            </DynamicLink>
            <div className="flex flex-col">
                {isPressroom && datePublished && (
                    <Text weight="regular" size="small" className="text-main-dark-grey">
                        {formattedDate}
                    </Text>
                )}
                {!pressromPageArticle && (
                    <StoryText size="size4">{article?.articlePreviewText}</StoryText>
                )}
            </div>
            <ButtonText
                asElement={DynamicLink}
                href={articleHref}
                prefetch={false}
                tabIndex={-1}
                aria-hidden="true"
                size="big"
                icon={<IconArrowRight className="h-16 w-16" />}
                iconPosition="right"
                className="flex items-center gap-4 p-14 pl-0"
            >
                {t(language, "common.read_more")}
            </ButtonText>
        </article>
    )
}
